import { AddonGroup } from 'models/AddonGroup';
import { Button } from 'components/Button';
import { Reducer, useReducer, useEffect } from 'react';
import { RelatedProduct } from 'models/RelatedProduct';
import { imageBasePath, ResourceTexts } from '../../selector';
import { ElfaDoorSpec } from 'models/ElfaDoorSpec';
import { NO_ELEMENT, rm } from 'utils';
import cn from 'classnames';
import { Checkbox } from 'components/Shared/Form';
import { fillingMap } from '../../fillingMap';
import { RedDrawer } from 'components/Shared/Drawer';
import { AccordionSection } from 'components/Accordion';
import styles from './styles.scss';

interface OwnProps extends ResourceTexts {
  addons: AddonGroup;
  doorId: number;
  defaultFilling: ElfaDoorSpec | undefined;
  cdnUrl: string;
  selectDoorFilling: (
    name: string,
    type: string,
    productId: string,
    artNo: string,
    doorId: number,
    useOnAllDoors: boolean
  ) => void;
}

interface FillingItem {
  name: string;
  productId: string;
  expanded: boolean;
  presentationPrice: number;
  currencyUnit: string;
}

interface OwnState {
  selected: boolean;
  fillings: FillingItem[];
  useOnAllDoors: boolean;
}

interface SelectedItem {
  name: string;
  image: string;
}

type OwnAction =
  | { type: 'toggle'; selected: boolean }
  | { type: 'toggleUseOnAllDoors'; selected: boolean }
  | { type: 'addFillings'; products: RelatedProduct[]; defaultFilling: ElfaDoorSpec | undefined }
  | { type: 'toggleFillType'; fillingType: string };

const ownReducer: Reducer<OwnState, OwnAction> = (state, action) => {
  switch (action.type) {
    case 'toggle':
      return {
        ...state,
        selected: action.selected
      };
    case 'toggleUseOnAllDoors':
      return {
        ...state,
        useOnAllDoors: action.selected
      };
    case 'addFillings':
      return {
        ...state,
        fillings: getFillings(action.products, action.defaultFilling)
      };
    case 'toggleFillType':
      return {
        ...state,
        fillings: state.fillings.map(f => {
          if (f.name.toLowerCase() === action.fillingType.toLowerCase()) {
            return { ...f, expanded: !f.expanded };
          }
          return { ...f };
        })
      };
    default:
      return state;
  }
};

const getFillings = (products: RelatedProduct[], defaultFilling: ElfaDoorSpec | undefined) => {
  if (!products) {
    return [];
  }

  return products.map(p => ({
    name: p.title,
    productId: p.productId,
    expanded: p.title.toLowerCase() === (defaultFilling ? defaultFilling.type.toLowerCase() : ''),
    presentationPrice: p.price.presentationPrice,
    currencyUnit: p.price.currencyUnit
  }));
};

const initialState: OwnState = {
  selected: false,
  fillings: [],
  useOnAllDoors: false
};

export default function DoorFilling({
  doorId,
  fillingModalDoorName,
  cdnUrl,
  addons,
  defaultFilling,
  selectDoorFilling,
  fillingModalHeader,
  fillingModalSame,
  fillingModalClose
}: OwnProps) {
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const doorNumber = `${doorId + 1}`;
  const doorName = `${fillingModalDoorName} ${doorNumber}`;
  const toggleModal = () => dispatch({ type: 'toggle', selected: !state.selected });
  const imageBaseUrl = `${cdnUrl}${imageBasePath}`;

  useEffect(() => {
    dispatch({
      type: 'addFillings',
      products: addons && addons.products,
      defaultFilling: defaultFilling ? defaultFilling : undefined
    });
  }, [addons, defaultFilling]);

  const selectFilling = (type: string, productId: string, artNo: string, name: string) => () => {
    selectDoorFilling(type, name, productId, artNo, doorId, state.useOnAllDoors);
    dispatch({ type: 'toggle', selected: false });
  };

  const toggleFillType = (type: string) => () => {
    dispatch({ type: 'toggleFillType', fillingType: type });
  };

  const isSelected = (type: string, name: string) => {
    if (defaultFilling) {
      return (
        defaultFilling.type.toLowerCase() === type.toLowerCase() &&
        defaultFilling.name.toLowerCase() === name.toLowerCase()
      );
    }

    return false;
  };

  const getImageItems = (item: FillingItem) => {
    if (!(item.name.toLowerCase() in fillingMap)) {
      return [];
    }

    const items = [];
    const map = fillingMap[item.name.toLowerCase()];

    for (const [key, value] of Object.entries(map)) {
      const selected = isSelected(item.name, key);
      const image = `${imageBaseUrl}${value.image}.png`;

      if (value && value.image) {
        items.push(
          <div
            key={key}
            onClick={selectFilling(item.name, item.productId, value.artNo, key)}
            className={cn(styles.modalDoorSelectButton, { [styles.selected]: selected })}
          >
            <div className={styles.doorPreviewImg} style={{ backgroundImage: `url(${image})` }} />
            <span className={selected ? styles.selected : ''}>{key}</span>
          </div>
        );
      }
    }

    return items;
  };

  const getSelectedItem = (): SelectedItem | undefined => {
    for (const [fKey, fValue] of Object.entries(fillingMap)) {
      for (const [key, value] of Object.entries(fValue)) {
        if (value) {
          const selected = isSelected(fKey, key);
          if (selected) {
            const image = `${imageBaseUrl}${value.image}.png`;
            return {
              name: key,
              image
            };
          }
        }
      }
    }

    return undefined;
  };

  const selectedItem = getSelectedItem();

  const toggleUseOnAllDoors = () => {
    dispatch({
      type: 'toggleUseOnAllDoors',
      selected: !state.useOnAllDoors
    });
  };

  return (
    <>
      {selectedItem && (
        <div className={styles.door}>
          <div className={styles.doorTitle}>
            {`${rm('product_configurator_elfa_door_label')} ${doorNumber}: `}
            <span className={styles.selectedItemName}>{selectedItem.name}</span>
          </div>

          <Button className={styles.doorButton} onClick={toggleModal} appearance="tertiary">
            <div
              className={styles.doorImage}
              style={{ backgroundImage: `url(${selectedItem.image})` }}
            />
            <div className={styles.buttonLabel}>{rm('checkout_overlay_button')}</div>
          </Button>
        </div>
      )}

      <RedDrawer
        isOpen={state.selected}
        title={`${fillingModalHeader} ${doorName}`}
        closeText={fillingModalClose}
        onClose={toggleModal}
        noPadding
      >
        <Checkbox
          className={styles.checkbox}
          appearance="tertiary"
          checked={state.useOnAllDoors}
          onChange={toggleUseOnAllDoors}
        >
          {fillingModalSame}
        </Checkbox>

        <div>
          {state.fillings.map(p => {
            const imageItems = getImageItems(p);
            if (imageItems.length === 0) {
              return NO_ELEMENT;
            }

            const title = (
              <span>
                {p.name} ({p.presentationPrice} {p.currencyUnit})
              </span>
            );

            return (
              <AccordionSection
                key={p.productId}
                title={title}
                setOpen={toggleFillType(p.name)}
                isOpen={p.expanded}
                contentContainerClassName={cn(styles.accordionContentContainer, {
                  [styles.open]: p.expanded
                })}
              >
                <div className={styles.fillings}>{imageItems}</div>
              </AccordionSection>
            );
          })}
        </div>
      </RedDrawer>
    </>
  );
}
